/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomValue } from '@cloudio-saas/statex';
import { Badge, Box, IconButton } from '@mui/material';
import { Platform } from 'componentregistry';
import { pageAtom } from 'designer/atoms';
import Icon from 'icons/Icon';
import React, {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useUpdateOnHMR } from 'utils/hmr';
import { getErrorMessage } from 'utils/isEmpty';
import WaveDots from 'utils/WaveDots';
import Analyze, { analyzeItem } from './Analyze';
import { AnalyzeIssue } from './AnalyzeUtils';

type Props = {
  appUid: string;
  pageId: string;
  platform: Platform;
};

export default function AnalyzeIcon({
  appUid,
  pageId,
  platform,
}: Props): React.ReactElement {
  const [showAnalyze, setShowAnalyze] = useState(false);
  const [busy, setBusy] = useState(true);
  const [loadedPageId, setLoadedPageId] = useState('');
  const _page = useStateXAtomValue(pageAtom, { params: { appUid, pageId } });
  const deferedValue = useMemo(() => {
    return { _page, pageId };
  }, [_page, pageId]);

  const { _page: page, pageId: _pageId } = useDeferredValue(deferedValue);
  const hmr = useUpdateOnHMR();
  const [[count, color, totalCount], setState] = useState<
    [number, 'info' | 'warning' | 'error', number]
  >([0, 'info', 0]);

  useLayoutEffect(() => {
    setLoadedPageId(pageId);
  }, [appUid, pageId]);

  useEffect(() => {
    async function analyzePage() {
      setBusy(true);
      if (page && hmr && _pageId === pageId && loadedPageId === pageId) {
        const item = page.root;
        let _collect: AnalyzeIssue[] = [];
        let _autoFixes: (() => void)[] = [];
        try {
          await analyzeItem({
            appUid,
            pageId,
            platform,
            page,
            item,
            alerts: _collect,
            autoFixes: _autoFixes,
          });
          _collect = _collect.filter((c) => !!c);
          _autoFixes = _autoFixes.filter((c) => !!c);
          const errorCount = _collect.filter(
            (c) => !c.severity || c.severity === 'error',
          ).length;
          if (errorCount > 0) {
            setState([errorCount, 'error', _collect.length]);
          } else {
            const warnCount = _collect.filter(
              (c) => c.severity === 'warning',
            ).length;
            if (warnCount > 0) {
              setState([warnCount, 'warning', _collect.length]);
            } else {
              setState([_collect.length, 'info', _collect.length]);
            }
          }
        } catch (e) {
          console.error(e);
          platform.showError(getErrorMessage(e));
        } finally {
          setBusy(false);
        }
      } else {
        setState([0, 'info', 0]);
      }
    }
    analyzePage();
  }, [page, hmr, _pageId, pageId, appUid, platform, loadedPageId]);

  return (
    <Box
      sx={{ cursor: count ? 'pointer' : 'default' }}
      data-tip={
        count
          ? count > 1
            ? `There are ${count} severity ${color} ${
                count < totalCount ? `out of ${totalCount} issues` : 'issues'
              }!`
            : `There is one ${color}${
                count < totalCount ? ` out of ${totalCount} issues` : ''
              }!`
          : 'Everything looks good so far!'
      }
      data-tip-error={count > 0 && color === 'error'}
      data-tip-width={600}
      onClick={() => (count ? setShowAnalyze(true) : undefined)}
    >
      <Badge
        color={busy ? 'default' : count ? color : 'success'}
        badgeContent={busy ? <WaveDots active /> : count || ' '}
        variant={count || busy ? 'standard' : 'dot'}
        overlap="circular"
      >
        <IconButton color="primary" disableRipple={count === 0}>
          <span aria-hidden="false" style={{ display: 'none' }}>
            Show Page Analysis Details
          </span>
          <Icon iconName="robot" />
        </IconButton>
      </Badge>

      {showAnalyze ? (
        <Analyze
          key={hmr}
          appUid={appUid}
          platform={platform}
          pageId={pageId}
          onClose={() => setShowAnalyze(false)}
        />
      ) : null}
    </Box>
  );
}
